/**
 * Deal Detail — slide-out panel showing full deal information.
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X, User, Package, Calendar, DollarSign,
  TrendingUp, Tag, Clock, CheckCircle, XCircle, Pencil, Check,
} from 'lucide-react'
import { useCrmStore } from '@/stores/crmStore'
import { useCRM } from '@/hooks/useCRM'
import { formatCents } from '@/utils/currency'
import { Button, Textarea } from '@/components/ui'
import type { Deal, Pipeline } from '@/types/crm'

interface DealDetailProps {
  deal: Deal
  pipeline: Pipeline | null
}

export function DealDetail({ deal, pipeline }: DealDetailProps) {
  const navigate = useNavigate()
  const setSelectedDeal = useCrmStore((s) => s.setSelectedDeal)
  const { moveDealStage, fetchDeals, fetchPipelineStats } = useCRM()
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [notesValue, setNotesValue] = useState(deal.notes || '')

  const stages = pipeline?.stages || []
  const currentStage = stages.find((s) => s.id === deal.stageId)
  const isWon = currentStage?.isWon || false
  const isLost = currentStage?.isLost || false

  const handleStageChange = useCallback(async (stageId: string) => {
    const stage = stages.find((s) => s.id === stageId)
    let lossReason: string | undefined
    if (stage?.isLost) {
      lossReason = window.prompt('Why was this deal lost?') || undefined
    }
    const result = await moveDealStage(deal.id, stageId, lossReason)
    if (result) {
      setSelectedDeal(result)
      fetchDeals()
      if (pipeline) fetchPipelineStats(pipeline.id)
    }
  }, [deal.id, stages, moveDealStage, setSelectedDeal, fetchDeals, fetchPipelineStats, pipeline])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground truncate">{deal.name}</h2>
            {deal.contactName && (
              <p className="text-sm text-muted-foreground mt-0.5">{deal.contactName}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Close deal detail"
            onClick={() => setSelectedDeal(null)}
            className="ml-2"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </Button>
        </div>

        {/* Stage badge + amount */}
        <div className="flex items-center gap-3 mt-3">
          {currentStage && (
            /* basis: hardcoded-surface guard — text-white sits on the admin-configured stage.color hex; keep fixed in both modes */
            <span
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full text-white"
              style={{ backgroundColor: currentStage.color }}
            >
              {currentStage.name}
            </span>
          )}
          <span className="text-lg font-bold text-foreground">
            {formatCents(deal.amountCents, deal.currency)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Details */}
        <Section title="Details">
          {deal.contactId && (
            <DetailRow icon={User} label="Contact" value={deal.contactName || deal.contactId}
              onClick={() => navigate(`/crm/contacts/${deal.contactId}`)} />
          )}
          <DetailRow icon={DollarSign} label="Amount" value={formatCents(deal.amountCents, deal.currency)} />
          {deal.expectedCloseDate && (
            <DetailRow icon={Calendar} label="Expected Close" value={deal.expectedCloseDate} />
          )}
          {deal.source && (
            <DetailRow icon={TrendingUp} label="Source" value={deal.source} />
          )}
          {deal.orderId && (
            <DetailRow icon={Package} label="Linked Order" value={deal.orderId}
              onClick={() => navigate(`/orders?order=${deal.orderId}`)} />
          )}
        </Section>

        {/* Stage selector */}
        <Section title="Move to Stage">
          <div className="flex flex-wrap gap-1.5">
            {stages.map((stage) => (
              <button
                key={stage.id}
                onClick={() => handleStageChange(stage.id)}
                disabled={stage.id === deal.stageId}
                /* basis: hardcoded-surface guard — active pill's text-white sits on the admin-configured stage.color hex (style below); keep fixed in both modes */
                className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
                  stage.id === deal.stageId
                    ? 'text-white'
                    : 'text-muted-foreground bg-muted hover:bg-muted-foreground/10'
                }`}
                style={stage.id === deal.stageId ? { backgroundColor: stage.color } : undefined}
              >
                {stage.name}
              </button>
            ))}
          </div>
        </Section>

        {/* Stage History */}
        {deal.stageHistory && deal.stageHistory.length > 0 && (
          <Section title="Stage History">
            <div className="space-y-1.5">
              {deal.stageHistory.map((entry, i) => {
                const isCurrentStage = i === deal.stageHistory!.length - 1 && !entry.exitedAt
                const duration = entry.exitedAt
                  ? getDuration(entry.enteredAt, entry.exitedAt)
                  : isCurrentStage
                    ? getDuration(entry.enteredAt, new Date().toISOString()) + ' (current)'
                    : ''
                return (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{entry.stageName || entry.stageId}</span>
                    {duration && <span className="text-muted-foreground">— {duration}</span>}
                  </div>
                )
              })}
            </div>
          </Section>
        )}

        {/* Tags */}
        {deal.tags && deal.tags.length > 0 && (
          <Section title="Tags">
            <div className="flex flex-wrap gap-1.5">
              {deal.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                  <Tag className="w-3 h-3 mr-1" />{tag}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Notes */}
        <Section title="Notes">
          {isEditingNotes ? (
            <div className="space-y-2">
              <Textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                className="resize-none"
                rows={4}
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" aria-label="Cancel editing notes" onClick={() => { setIsEditingNotes(false); setNotesValue(deal.notes || '') }}>
                  <X className="w-4 h-4 text-muted-foreground" />
                </Button>
                <Button variant="ghost" size="sm" aria-label="Save notes" onClick={() => setIsEditingNotes(false)}>
                  <Check className="w-4 h-4 text-success" />
                </Button>
              </div>
            </div>
          ) : (
            <div onClick={() => setIsEditingNotes(true)} className="cursor-pointer p-2 rounded-lg hover:bg-muted min-h-[40px]">
              {deal.notes ? (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{deal.notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Click to add notes
                </p>
              )}
            </div>
          )}
        </Section>

        {/* Quick actions */}
        {!isWon && !isLost && (
          <div className="flex gap-2">
            {stages.find((s) => s.isWon) && (
              <Button
                variant="ghost"
                onClick={() => handleStageChange(stages.find((s) => s.isWon)!.id)}
                leftIcon={<CheckCircle className="w-4 h-4" />}
                className="flex-1 bg-success/10 text-success hover:bg-success/20 hover:text-success"
              >
                Mark Won
              </Button>
            )}
            {stages.find((s) => s.isLost) && (
              <Button
                variant="ghost"
                onClick={() => handleStageChange(stages.find((s) => s.isLost)!.id)}
                leftIcon={<XCircle className="w-4 h-4" />}
                className="flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive"
              >
                Mark Lost
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{title}</h3>
      {children}
    </div>
  )
}

function DetailRow({ icon: Icon, label, value, onClick }: {
  icon: React.ElementType; label: string; value: string; onClick?: () => void
}) {
  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
      {onClick ? (
        <button onClick={onClick} className="text-sm text-primary hover:text-primary/80 truncate">{value}</button>
      ) : (
        <span className="text-sm text-foreground truncate">{value}</span>
      )}
    </div>
  )
}

function getDuration(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const diffMs = end.getTime() - start.getTime()
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  if (hours < 24) return hours < 1 ? '<1h' : `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}
